import React, { useState, useRef } from 'react';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

// ── Render @mentions as coloured chips in displayed text ──────────────────
export function renderMentions(text, team) {
  if (!text || !team || team.length === 0) return text;
  const names = team.map(m => m.name).filter(Boolean);
  if (names.length === 0) return text;
  const escaped = names.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp('(@(?:' + escaped.join('|') + '))', 'g');
  const parts = text.split(pattern);
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      const name = part.slice(1);
      const member = team.find(m => m.name === name);
      if (member) {
        return (
          <span key={i} style={{
            background: member.color + '1a',
            color: member.color,
            fontWeight: 700,
            borderRadius: 3,
            padding: '0 3px',
            fontSize: 'inherit',
          }}>
            {part}
          </span>
        );
      }
    }
    return part;
  });
}

// ── useMention hook ────────────────────────────────────────────────────────
// Returns everything needed to power a mention-aware input/textarea.
export function useMention(value, onChange, team) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const [idx, setIdx]     = useState(0);
  const inputRef          = useRef(null);

  const filtered = team.filter(m =>
    m.name.toLowerCase().startsWith(query.toLowerCase())
  );

  function handleChange(e) {
    const v   = e.target.value;
    const pos = e.target.selectionStart || v.length;
    const before   = v.slice(0, pos);
    const atMatch  = before.match(/@(\w*)$/);
    if (atMatch) {
      setOpen(true);
      setQuery(atMatch[1]);
      setIdx(0);
    } else {
      setOpen(false);
    }
    onChange(e);
  }

  function handleSelect(member) {
    const el = inputRef.current;
    if (!el) return;
    const pos    = el.selectionStart || value.length;
    const before = value.slice(0, pos);
    const atMatch = before.match(/@(\w*)$/);
    if (!atMatch) return;
    const start  = pos - atMatch[0].length;
    const newVal = value.slice(0, start) + '@' + member.name + ' ' + value.slice(pos);
    onChange({ target: { value: newVal } });
    setOpen(false);
    setTimeout(() => {
      if (!el) return;
      el.focus();
      const newPos = start + member.name.length + 2;
      el.setSelectionRange(newPos, newPos);
    }, 0);
  }

  // Returns true if keydown was consumed (caller should skip its own handler)
  function handleKeyDown(e) {
    if (!open || filtered.length === 0) return false;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIdx(i => Math.min(i + 1, filtered.length - 1));
      return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIdx(i => Math.max(i - 1, 0));
      return true;
    }
    if (e.key === 'Enter' && filtered[idx]) {
      e.preventDefault();
      handleSelect(filtered[idx]);
      return true;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      return true;
    }
    return false;
  }

  return { open, query, idx, filtered, handleChange, handleSelect, handleKeyDown, inputRef };
}

// ── MentionDropdown ────────────────────────────────────────────────────────
export function MentionDropdown({ open, filtered, idx, onSelect, above = true }) {
  if (!open || filtered.length === 0) return null;
  return (
    <div style={{
      position: 'absolute',
      [above ? 'bottom' : 'top']: 'calc(100% + 4px)',
      left: 0, right: 0,
      background: '#fff',
      border: '1px solid #d8e4ea',
      borderRadius: 9,
      boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
      zIndex: 200,
      overflow: 'hidden',
    }}>
      <div style={{ padding: '5px 10px 3px', fontSize: '.6rem', color: '#9eb8c8', letterSpacing: '.05em', textTransform: 'uppercase', fontWeight: 600, fontFamily: F, borderBottom: '1px solid #f0f2f4' }}>
        Mention a teammate
      </div>
      {filtered.map((m, i) => (
        <div
          key={m.email}
          onMouseDown={e => { e.preventDefault(); onSelect(m); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 9,
            padding: '8px 12px', cursor: 'pointer',
            background: i === idx ? '#edf4f8' : 'transparent',
            fontFamily: F,
          }}
        >
          <div style={{
            width: 26, height: 26, borderRadius: '50%',
            background: m.color, color: m.text,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '.58rem', fontWeight: 700, flexShrink: 0,
          }}>
            {m.initials}
          </div>
          <div>
            <div style={{ fontSize: '.78rem', fontWeight: 500, color: '#0f1e28', lineHeight: 1.2 }}>{m.name}</div>
            <div style={{ fontSize: '.62rem', color: '#9eb8c8' }}>{m.email}</div>
          </div>
          <div style={{ marginLeft: 'auto', fontSize: '.6rem', color: '#c8d8e0', fontWeight: 500 }}>
            @{m.name}
          </div>
        </div>
      ))}
    </div>
  );
}
